package er.prateek.crossly.progression.achievements

import androidx.compose.runtime.mutableStateListOf
import androidx.compose.runtime.mutableStateMapOf
import er.prateek.crossly.firebase.AnalyticsEvents
import er.prateek.crossly.firebase.AnalyticsLogger
import er.prateek.crossly.persistence.AchievementStore
import kotlin.time.Clock

/**
 * Owns which badges are unlocked and the queue of unlocks still waiting to be celebrated.
 *
 * Unlocking is rule-based ([Achievement.unlockedBy] over a [PlayerStats] snapshot) rather than
 * event-based, so a badge can never be missed because some screen forgot to fire an event --
 * any later evaluation catches it up. Compose-observable: [unlocked], [unseen] and [pending] are
 * snapshot state.
 */
class AchievementManager(
    private val store: AchievementStore,
    private val analytics: AnalyticsLogger,
    private val clock: Clock = Clock.System,
) {
    private val unlockedAt = mutableStateMapOf<String, Long>().apply { putAll(store.unlocked()) }
    private val unseenIds = mutableStateListOf<String>().apply { addAll(store.unseen()) }

    /** Unlocks waiting for their celebration, oldest first. The UI pops them with [consumeCelebration]. */
    val pending = mutableStateListOf<Achievement>()

    fun isUnlocked(achievement: Achievement): Boolean = achievement.id in unlockedAt

    fun unlockedAtMs(achievement: Achievement): Long? = unlockedAt[achievement.id]

    fun unlockedCount(): Int = Achievement.entries.count { isUnlocked(it) }

    fun isUnseen(achievement: Achievement): Boolean = achievement.id in unseenIds

    fun hasUnseen(): Boolean = unseenIds.isNotEmpty()

    /** Most recent unlocks first, then locked badges in catalogue order -- the Home shelf order. */
    fun shelf(): List<Achievement> =
        Achievement.entries.filter { isUnlocked(it) }.sortedByDescending { unlockedAt[it.id] } +
            Achievement.entries.filterNot { isUnlocked(it) }

    /**
     * First run only: unlock everything the player has *already* earned without celebrating it, so
     * an existing player isn't buried under a dozen pop-ups the first time this build opens.
     */
    fun seedIfNeeded(stats: PlayerStats) {
        if (store.isSeeded()) return
        val earned = Achievement.entries.filter { !isUnlocked(it) && it.unlockedBy(stats) }
        val now = clock.now().toEpochMilliseconds()
        earned.forEach { unlockedAt[it.id] = now }
        if (earned.isNotEmpty()) store.saveUnlocked(unlockedAt.toMap())
        store.markSeeded()
    }

    /** Unlocks every newly satisfied badge and queues each for celebration. Returns the new ones. */
    fun evaluate(stats: PlayerStats): List<Achievement> {
        seedIfNeeded(stats)
        val fresh = Achievement.entries.filter { !isUnlocked(it) && it.unlockedBy(stats) }
        if (fresh.isEmpty()) return fresh
        val now = clock.now().toEpochMilliseconds()
        fresh.forEach { achievement ->
            unlockedAt[achievement.id] = now
            unseenIds += achievement.id
            analytics.logEvent(
                AnalyticsEvents.ACHIEVEMENT_UNLOCKED,
                mapOf(
                    "achievement_id" to achievement.id,
                    "rarity" to achievement.rarity.name,
                    "unlocked_count" to unlockedAt.size,
                ),
            )
        }
        store.saveUnlocked(unlockedAt.toMap())
        store.saveUnseen(unseenIds.toSet())
        // Rarest first: if a single solve earns several badges, lead with the biggest moment.
        pending += fresh.sortedByDescending { it.rarity.ordinal }
        return fresh
    }

    fun consumeCelebration(achievement: Achievement) {
        pending.remove(achievement)
    }

    fun markAllSeen() {
        if (unseenIds.isEmpty()) return
        unseenIds.clear()
        store.saveUnseen(emptySet())
    }

    fun recordBrainstormRound(): Int = store.incrementBrainstormRounds()

    fun brainstormRounds(): Int = store.brainstormRounds()

    fun reset() {
        store.resetAll()
        unlockedAt.clear()
        unseenIds.clear()
        pending.clear()
    }
}
